import { useState } from 'react';
import type { ReactElement } from 'react';
import { paisa, type StatusPresentation } from '@fleet/shared';
import { useInvoicesQuery } from '@/api/endpoints';
import type { Invoice } from '@/api/types';
import { MoneyText } from '@/design/Data';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { Button, LinkButton } from '@/design/Button';
import { Field, TextInput } from '@/design/Field';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import { InvoiceModal } from './InvoiceModal';

/**
 * Invoices.
 *
 * A list of documents rather than a list of records, which changes what belongs
 * on it: the number, who it is for, what it says, and whether it still counts.
 * Opening one shows the document itself — the thing the customer is holding —
 * because that is what somebody is checking against when they ring up about it.
 *
 * Nothing here edits an invoice. An issued invoice is immutable; correcting one
 * means voiding it and raising another, because the customer already has a copy
 * of the first and two papers with one number is the problem the number exists
 * to prevent.
 */

function statusPill(invoice: Invoice): StatusPresentation {
  if (invoice.status === 'void') return { label: 'Void', tone: 'neutral', shape: 'cross' };
  if (invoice.status === 'draft') return { label: 'Draft', tone: 'attention', shape: 'ring' };
  return { label: 'Issued', tone: 'active', shape: 'square' };
}

export function InvoicesPage(): ReactElement {
  const [search, setSearch] = useState('');
  const [showing, setShowing] = useState<'all' | 'issued'>('all');
  const [open, setOpen] = useState<Invoice | null>(null);

  const query = useInvoicesQuery({
    ...(search.trim().length >= 2 ? { q: search.trim() } : {}),
    ...(showing === 'issued' ? { status: 'issued' } : {}),
  });

  const invoices = query.data?.results ?? [];
  const issuedTotal = invoices
    .filter((invoice) => invoice.status === 'issued')
    .reduce((sum, invoice) => sum + invoice.total, 0);

  const columns: readonly Column<Invoice>[] = [
    {
      key: 'number',
      header: 'Number',
      render: (invoice) => (
        <div className="flex flex-col">
          <span className="identifier font-medium">{invoice.number}</span>
          {/* Both calendars, because both get asked for. */}
          <span className="text-micro text-ink-muted">{invoice.issued_at_bs ?? 'Not issued'}</span>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (invoice) => (
        <div className="flex flex-col">
          <span>{invoice.customer_name}</span>
          <span className="identifier text-micro text-ink-muted">{invoice.booking_reference}</span>
        </div>
      ),
    },
    {
      key: 'vat',
      header: 'Of which VAT',
      numeric: true,
      secondary: true,
      render: (invoice) =>
        invoice.vat === 0 ? (
          <span className="text-ink-faint">—</span>
        ) : (
          <MoneyText amount={paisa(invoice.vat)} />
        ),
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      render: (invoice) => (
        <span className={invoice.status === 'void' ? 'text-ink-faint line-through' : 'font-semibold'}>
          <MoneyText amount={paisa(invoice.total)} />
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (invoice) => <StatusPill status={statusPill(invoice)} />,
    },
    {
      key: 'actions',
      header: '',
      render: (invoice) => (
        <div className="flex justify-end">
          <Button onClick={() => setOpen(invoice)}>Open</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">Invoices</h1>

      <Panel
        title="Documents given to customers"
        // QA's question, answered where it was asked. There is no "new
        // invoice" button here on purpose: an invoice is raised *from* a
        // booking, because the booking is what supplies the lines, the
        // customer and the figures. Saying so is better than a button that
        // would only ask which booking.
        note="To make one: open a booking and press “Raise an invoice”. An issued invoice is never edited — it is voided and replaced."
        current
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-small text-ink-muted">
              Issued on this page: <MoneyText amount={paisa(issuedTotal)} />
            </span>
            <Button
              intent={showing === 'all' ? 'primary' : 'secondary'}
              onClick={() => setShowing('all')}
            >
              All
            </Button>
            <Button
              intent={showing === 'issued' ? 'primary' : 'secondary'}
              onClick={() => setShowing('issued')}
            >
              Issued only
            </Button>
          </div>
        }
      >
        <div className="border-b border-rule px-3 py-3 sm:px-4">
          <Field label="Search by invoice number or customer name">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                describedBy={describedBy}
                value={search}
                onChange={setSearch}
              />
            )}
          </Field>
        </div>

        <DataTable
          columns={columns}
          rows={invoices}
          rowKey={(invoice) => invoice.id}
          isLoading={query.isLoading}
          isError={query.isError}
          onRetry={() => void query.refetch()}
          errorHeadline="The invoices did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline: search === '' ? 'No invoices yet' : 'Nothing matches that',
            detail:
              search === ''
                ? 'Open the booking you want to bill and press “Raise an invoice”. It gets the next number in the Nepali fiscal year and appears here straight away.'
                : 'Try the invoice number, or part of the customer’s name.',
            action:
              search === '' ? (
                <LinkButton to="/bookings" intent="primary">
                  Go to bookings
                </LinkButton>
              ) : undefined,
          }}
        />
      </Panel>

      <InvoiceModal invoice={open} onClose={() => setOpen(null)} />
    </div>
  );
}
